// ═══════════════════════════════════════════════════════════════════════════
// Timbrado por el servidor — se acaban los proxies
//
// POR QUÉ EXISTÍAN LOS PROXIES: Facturapi no permite llamadas desde un
// navegador (no manda cabeceras CORS), así que la app pasaba por
// corsproxy.io / allorigins.win / el Worker. Eso arrastraba tres problemas:
//
//   1. La llave viajaba en el navegador Y por un tercero ajeno.
//   2. Cualquier firewall estricto tumbaba el timbrado, porque los proxies
//      son dominios de terceros que suelen estar bloqueados. Es exactamente
//      lo que le pasó al operador.
//   3. El proxy del Worker (`/fapi/`) quedó abierto sin autenticación.
//
// AHORA: el navegador llama a NUESTRA función de Supabase, y esa función habla
// con Facturapi desde el servidor. Supabase sí manda CORS, así que no hace
// falta ningún intermediario. La llave no baja nunca.
//
// El navegador manda "timbra la remisión 418"; NO manda el comprobante. El
// CFDI se arma desde Postgres, así que nadie puede timbrar importes distintos
// a los del documento guardado.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from "../auth/sesion.js";
import { configSupabase } from "../auth/config.js";
import { verOdescargar, compartirOdescargar, verConAdjuntos, esIOS } from "../pdf/guardar.js";

/**
 * ¿Se puede timbrar por el servidor?
 *
 * Hace falta sesión, inquilino y que la remisión ya esté en Postgres — la
 * función arma el CFDI desde ahí. Una remisión que todavía espera folio no
 * está: primero tiene que subirla el sincronizador.
 */
export function viaDeTimbrado({ fuente, tenantId, remision }) {
  if (fuente !== "postgres")
    return { via: "navegador", motivo: "La fuente de datos es este dispositivo." };
  if (!tenantId)
    return { via: "navegador", motivo: "No hay vinícola en la sesión." };
  if (remision?._pendienteFolio)
    return { via: "navegador",
             motivo: "La remisión todavía no sube a Postgres: espera a que tenga folio." };
  return { via: "servidor", motivo: null };
}

async function llamar(cuerpo, { binario = false } = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("No hay sesión iniciada.");

  // Del config, no de propiedades internas del cliente de Supabase: esas no
  // son API pública y cambiarían de nombre sin avisar.
  const { url, anonKey } = configSupabase();

  const r = await fetch(`${url.replace(/\/$/, "")}/functions/v1/timbrar`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${session.access_token}`,
      "apikey": anonKey,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(cuerpo),
  });

  if (binario) {
    if (!r.ok) {
      // Los errores sí vienen en JSON aunque se esperara un archivo.
      let msg = `El servidor devolvió ${r.status}`;
      try { msg = (await r.json())?.error || msg; } catch { /* cuerpo no-JSON */ }
      throw new Error(msg);
    }
    return await r.blob();
  }

  const res = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(res?.error || `El servidor devolvió ${r.status}`);
    e.status = r.status;
    e.detalle = res;
    e.uuid = res?.uuid;          // por si timbró y falló al registrar
    throw e;
  }
  return res;
}

/**
 * Timbra una remisión. Devuelve la MISMA forma que el camino del navegador
 * para que la interfaz no tenga que distinguir cuál se usó.
 *
 * `iepsDisplay` es cómo se presenta el IEPS en ESTE comprobante: 'incluido'
 * (absorbido en el valor unitario) o 'desglosado' (traslado aparte, para quien
 * lo acredita). El total es el mismo; sin él, manda el de la remisión.
 */
export async function timbrarEnServidor(inv, tenantId, { iepsDisplay } = {}) {
  const res = await llamar({
    accion: "timbrar",
    remision_legacy: inv.id,
    tenant_id: tenantId,
    ...(iepsDisplay ? { ieps_display: iepsDisplay } : {}),
  });
  return {
    id: res.facturapi_id,
    uuid: res.uuid,
    // Facturapi usa "valid"; la base usa "vigente". La app compara contra
    // "canceled", así que se normaliza a lo que ya tenían las 22 remisiones
    // timbradas antes de esto.
    status: "valid",
    verification_url: res.verification_url,
    total: res.total,
    _servidor: true,
    _esLive: res.es_live,
    _iepsDisplay: res.ieps_display,
    _aviso: res.aviso,
  };
}

/**
 * Cancela un CFDI ante el SAT, con la llave del servidor.
 *
 * `pendiente: true` quiere decir que solo se SOLICITÓ: el receptor tiene que
 * aceptar, y hasta entonces el CFDI sigue vigente.
 */
export async function cancelarEnServidor(cfdiId, motivo, tenantId, sustituye = null) {
  const res = await llamar({
    accion: "cancelar", cfdi_id: cfdiId, motivo, tenant_id: tenantId,
    ...(sustituye ? { sustituye } : {}),
  });
  return { status: res.pendiente ? "pending" : (res.status || "canceled"),
           pendiente: !!res.pendiente };
}

/**
 * ¿En qué va una cancelación que espera al receptor?
 * `estado`: 'cancelado' | 'pendiente' | 'rechazada' | 'sin_solicitud'.
 */
export async function consultarCancelacion(cfdiId, tenantId) {
  return await llamar({ accion: "estado_cancelacion", cfdi_id: cfdiId, tenant_id: tenantId });
}

/** Baja el PDF, el XML o el acuse. `formato`: 'pdf' | 'xml' | 'acuse'. */
/** Trae el archivo del CFDI sin hacer nada con él: `{ blob, nombre }`. */
export async function obtenerDelServidor(cfdiId, formato, tenantId) {
  const blob = await llamar(
    { accion: "descargar", cfdi_id: cfdiId, formato, tenant_id: tenantId },
    { binario: true });
  const ext = formato === "xml" ? "xml" : "pdf";
  const nombre = formato === "acuse"
    ? `Acuse_cancelacion_${cfdiId}.pdf`
    : `CFDI_${cfdiId}.${ext}`;
  return { blob, nombre };
}

export async function descargarDelServidor(cfdiId, formato, tenantId) {
  // En iPhone el XML suelto no tiene salida: la hoja de compartir caduca
  // mientras se espera al servidor y lo único que queda es una pestaña con la
  // URL. Va al visor de su PDF, donde se comparte con un toque propio.
  if (formato === "xml" && esIOS())
    return await verCfdiConXml(cfdiId, tenantId);
  const { blob, nombre } = await obtenerDelServidor(cfdiId, formato, tenantId);
  // Un XML no se mira: se comparte o se descarga, sin visor ni pestaña nueva.
  // El PDF y el acuse, al visor.
  if (formato === "xml") await compartirOdescargar(blob, nombre);
  else verOdescargar(blob, nombre);
}

/**
 * El PDF de un CFDI en el visor, con su XML a bordo: un botón comparte o
 * descarga los dos. Sirve igual para una factura que para un complemento.
 */
export async function verCfdiConXml(cfdiId, tenantId, titulo = "") {
  const [pdf, xml] = await Promise.all([
    obtenerDelServidor(cfdiId, "pdf", tenantId),
    obtenerDelServidor(cfdiId, "xml", tenantId),
  ]);
  await verConAdjuntos(pdf, [xml], titulo);
}

/**
 * Lo ÚNICO que el navegador puede saber de la llave: si existe, si es de
 * producción y si hay carta responsiva. Nunca el valor.
 */
export async function estadoLlaveFiscal(tenantId) {
  const { data, error } = await supabase.rpc("estado_llave_fiscal", { p_tenant: tenantId });
  if (error) return null;
  return data;
}


/**
 * Guardar una llave fiscal NUEVA. Va directo al vault del servidor.
 *
 * No hay función hermana que la lea: `leer_llave_fiscal` sigue revocada para el
 * navegador y solo la usa el servicio de timbrado. Se puede escribir y no se
 * puede sacar, que es justamente el punto.
 *
 * La llave NO se guarda en `localStorage` ni se devuelve en la respuesta.
 */
export async function rotarLlaveFiscal(tenantId, apiKey, esLive, orgId = null) {
  const { data, error } = await supabase.rpc("rotar_llave_fiscal", {
    p_tenant: tenantId, p_api_key: apiKey, p_is_live: !!esLive, p_org_id: orgId,
  });
  if (error) throw new Error(error.message);
  return data;
}


/**
 * ¿La llave que hay en el servidor sirve?
 *
 * Existe por un hueco que se veía venir: al rotar la llave, la única prueba de
 * que quedó bien era timbrar de verdad — o sea descubrir el error con un CFDI
 * de por medio y un cliente esperando.
 *
 * Le pregunta a Facturapi por la organización de la llave. Es una lectura: no
 * crea nada, no timbra nada, no cuesta nada.
 */
export async function verificarLlaveFiscal(tenantId) {
  return await llamar({ accion: "verificar", tenant_id: tenantId });
}


/**
 * Emitir el complemento de pago (REP) de un pago recibido.
 *
 * Todo lo que el SAT exige —UUID relacionado, parcialidad, saldo anterior,
 * importe— lo DERIVA el servidor de los pagos ya registrados. Aquí solo se dice
 * de qué pago se trata: si el navegador mandara las cifras, podría declarar
 * ante el SAT importes distintos a los del documento.
 */
export async function emitirComplemento(tenantId, pagoLegacyId) {
  return await llamar({ accion: "complemento", tenant_id: tenantId,
                        pago_legacy: pagoLegacyId });
}
